/**
 * Unified error fallback component with multiple display variants.
 * @module error/ErrorFallback
 */
import { Alert, Button, Code, Collapse, ScrollArea, Stack, Text } from '@mantine/core';
import { useToggle } from '@mantine/hooks';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import classes from './ErrorFallback.module.css';
import { getErrorMessage } from './utils.ts';

type ErrorFallbackVariant = 'alert' | 'inline' | 'minimal';

export interface ErrorFallbackProps extends Partial<FallbackProps> {
  error: unknown;
  resetErrorBoundary?: () => void;
  title?: string;
  context?: string;
  variant?: ErrorFallbackVariant;
  showRetry?: boolean;
  showDetails?: boolean;
  retryAction?: ReactNode;
  className?: string;
}

/**
 * Unified error fallback component with three display variants:
 *
 * - `alert` (default): Full Mantine Alert with icon, optional retry button, optional technical details
 * - `inline`: Lightweight div structure for grid cells (red title, gray message)
 * - `minimal`: Plain text only, no container or styling
 *
 * @example Alert variant with retry
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   resetErrorBoundary={reset}
 *   title="Chart failed to render"
 *   context="Type: Line | Rows: 50"
 *   showRetry
 *   showDetails
 * />
 * ```
 *
 * @example Inline variant for grid cells
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   title="Widget Error"
 *   variant="inline"
 * />
 * ```
 *
 * @example Minimal variant
 * ```tsx
 * <ErrorFallback error={error} variant="minimal" />
 * ```
 *
 * @example Custom retry action
 * ```tsx
 * <ErrorFallback
 *   error={error}
 *   retryAction={<ActionBarPrimitive.Reload />}
 * />
 * ```
 */
export function ErrorFallback({
  error,
  resetErrorBoundary,
  title,
  context,
  variant = 'alert',
  showRetry = false,
  showDetails = false,
  retryAction,
  className,
}: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [detailsOpen, toggleDetailsOpen] = useToggle();
  const resolvedTitle = title ?? t('error');
  const errorMessage = getErrorMessage(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  if (variant === 'minimal') {
    return <span className={className}>{errorMessage}</span>;
  }

  if (variant === 'inline') {
    return (
      <div className={clsx(classes.inline, className)}>
        <div className={classes.inlineTitle}>{resolvedTitle}</div>
        <div className={classes.inlineMessage}>{errorMessage}</div>
      </div>
    );
  }

  return (
    <Alert
      className={className}
      color="red"
      icon={<IconAlertTriangle color="var(--mantine-color-red-5)" size={20} />}
      p={0}
      title={resolvedTitle}
      variant="transparent"
    >
      <Stack gap="xs">
        {context && (
          <Text fz="sm" m={0}>
            {context}
          </Text>
        )}
        <Text c="red.2" fz="sm" m={0}>
          {errorMessage}
        </Text>
        {showDetails && errorStack && (
          <>
            <Button color="red" onClick={() => toggleDetailsOpen()} size="xs" variant="light">
              {detailsOpen ? t('hideDetails') : t('showDetails')}
            </Button>
            <Collapse expanded={detailsOpen}>
              <Code block className={classes.detailsContent}>
                <ScrollArea.Autosize mah={200} type="auto">
                  {errorStack}
                </ScrollArea.Autosize>
              </Code>
            </Collapse>
          </>
        )}
        {showRetry &&
          (retryAction ?? (
            <Button leftSection={<IconRefresh size={16} />} onClick={resetErrorBoundary} size="sm" variant="light">
              {t('retry')}
            </Button>
          ))}
      </Stack>
    </Alert>
  );
}
